/**
 * Color Picker — Foraging Toy (visual per-lid customizer)
 *
 * Flow:
 *   1. Core row: first 4 colors (all lids match) + right-aligned "Mix your own" chip
 *   2. "+N colors" toggle expands remaining single-color options
 *   3. "Mix your own" chip opens the visual per-lid builder (+€2,99)
 *
 * When in per-lid mode:
 *   - Shows an SVG top-down view of the foraging board with 6 clickable lids
 *   - Users click a lid (or the board for Base), then pick a color from the palette
 *   - Switches the hidden variant to "Custom" (higher price variant)
 *   - Populates line item properties (Lid 1: Purple, Lid 2: Green, … Base: White)
 *
 * When in standard mode (core or expanded single-color):
 *   - Uses the normal variant system (one variant per color)
 *   - No line item properties needed
 */

if (!customElements.get('color-picker-foraging')) {
  class ColorPickerForaging extends HTMLElement {
    constructor() {
      super();
      this.cta = null;
      this.workshopWrap = null;
      this.nameDisplay = null;
      this.upsellBtn = null;
      this.lidBuilderWrap = null;
      this.toySvg = null;
      this.activeLabel = null;
      this.resetBtn = null;
      this.paletteContainer = null;
      this.productForm = null;

      // State
      this.isWorkshopOpen = false;
      this.isCustomMode = false;
      this.activeComponentIdx = 0; // 0-5 = lids, 6 = base
      this.lidCount = 6;
      // Tracks which lids the user has explicitly coloured this session.
      // Used so auto-advance wraps back to skipped lids before jumping to base.
      this.visitedLids = new Set();
      this.lidColors = []; // Array of {name, rgb} per lid
      this.baseColor = { name: 'White', rgb: 'rgb(255, 255, 255)' };
      this.defaultColor = { name: 'Grey', rgb: 'rgb(158, 158, 158)' };
    }

    connectedCallback() {
      // Core elements (same as color-picker-workshop)
      this.cta = this.querySelector('[data-cta]');
      this.workshopWrap = this.querySelector('[data-workshop-wrap]');
      this.nameDisplay = this.querySelector('[data-selected-name]');

      // Per-lid builder elements
      this.upsellBtn = this.querySelector('[data-upsell-cta]');
      this.lidBuilderWrap = this.querySelector('[data-lid-builder-wrap]');
      this.toySvg = this.querySelector('[data-toy-svg]');
      this.activeLabel = this.querySelector('[data-active-label]');
      this.resetBtn = this.querySelector('[data-toy-reset]');
      this.paletteContainer = this.querySelector('[data-lid-palette]');

      // Product form (for line item properties)
      const formId = this.dataset.productFormId;
      if (formId) {
        this.productForm = document.getElementById(formId);
      }

      // Get lid count from data attribute
      this.lidCount = parseInt(this.dataset.lidCount) || 6;

      // Read default color from current selection
      const currentName = this.nameDisplay?.textContent?.trim() || 'Grey';
      const currentRgb = this.getColorRgbByName(currentName);
      this.defaultColor = { name: currentName, rgb: currentRgb };

      // Initialize lid colors from current selection
      this.lidColors = Array(this.lidCount).fill(null).map(() => ({
        name: this.defaultColor.name,
        rgb: this.defaultColor.rgb
      }));
      this.baseColor = { name: 'White', rgb: 'rgb(255, 255, 255)' };

      // Locale-aware label templates from data attributes
      // e.g. "Lid __N__" (EN), "Deksel __N__" (NL), "Deckel __N__" (DE)
      this.lidLabelTemplate = this.dataset.lidLabelTpl || 'Lid __N__';
      this.baseLabelText = this.dataset.baseLabel || 'Base';

      // Event listeners
      if (this.cta && this.workshopWrap) {
        this.cta.addEventListener('click', this.toggleWorkshop.bind(this));
      }

      if (this.upsellBtn) {
        this.upsellBtn.addEventListener('click', this.toggleCustomMode.bind(this));
      }

      // SVG toy click handler
      if (this.toySvg) {
        this.toySvg.addEventListener('click', this.handleToyClick.bind(this));
      }

      // Reset button
      if (this.resetBtn) {
        this.resetBtn.addEventListener('click', this.handleReset.bind(this));
      }

      if (this.paletteContainer) {
        this.paletteContainer.addEventListener('click', this.handlePaletteClick.bind(this));
      }

      // Close button on the custom-mode header (matches the wheel pattern)
      const closeBtn = this.querySelector('[data-close-custom]');
      if (closeBtn) {
        closeBtn.addEventListener('click', this.exitCustomMode.bind(this));
      }

      // Unified swatch handler (core + workshop)
      this.addEventListener('click', this.handleSwatchClick.bind(this));

      // Restore states
      this.restoreOpenState();
    }

    // ---- Workshop open/close (same as base) ----

    restoreOpenState() {
      const productInfo = this.closest('product-info');
      if (productInfo && productInfo.dataset.workshopOpen === 'true') {
        if (this.workshopWrap) {
          this.workshopWrap.classList.add('is-open', 'no-animate');
          this.isWorkshopOpen = true;
        }
        if (this.cta) {
          this.cta.classList.add('is-open');
          const openText = this.cta.dataset.textOpen;
          if (openText) {
            const textEl = this.cta.querySelector('[data-cta-text]');
            if (textEl) textEl.textContent = openText;
          }
        }
      }

      // Restore custom mode
      if (productInfo && productInfo.dataset.customMode === 'true') {
        this.enterCustomMode(false); // false = don't animate
      }
    }

    persistOpenState(isOpen) {
      const productInfo = this.closest('product-info');
      if (productInfo) {
        productInfo.dataset.workshopOpen = isOpen ? 'true' : 'false';
      }
    }

    persistCustomMode(isCustom) {
      const productInfo = this.closest('product-info');
      if (productInfo) {
        productInfo.dataset.customMode = isCustom ? 'true' : 'false';
      }
    }

    toggleWorkshop() {
      if (this.workshopWrap.classList.contains('no-animate')) {
        this.workshopWrap.classList.remove('no-animate');
      }

      this.isWorkshopOpen = this.workshopWrap.classList.toggle('is-open');
      this.cta.classList.toggle('is-open', this.isWorkshopOpen);
      this.persistOpenState(this.isWorkshopOpen);

      // Update CTA text
      const openText = this.cta.dataset.textOpen;
      const closedText = this.cta.dataset.textClosed;
      const textEl = this.cta.querySelector('[data-cta-text]');
      if (textEl) textEl.textContent = this.isWorkshopOpen ? openText : closedText;

      // Replay staggered animation on open
      if (this.isWorkshopOpen) {
        const swatches = this.workshopWrap.querySelectorAll('.color-picker__named-swatch');
        swatches.forEach((s) => {
          s.style.animation = 'none';
          void s.offsetHeight;
          s.style.animation = '';
        });
      }

      // Workshop and per-lid builder are independent — closing the
      // workshop does not close the per-lid builder.
    }

    // ---- Standard swatch click (core + workshop, all lids match) ----

    handleSwatchClick(e) {
      const btn = e.target.closest('[data-color-swatch]');
      if (!btn) return;

      // Don't handle clicks inside the per-lid palette
      if (btn.closest('[data-lid-palette]')) return;

      const colorName = btn.dataset.colorName;
      const radioId = btn.dataset.radioTarget;
      if (!colorName || !radioId) return;

      // Exit custom mode — we're going back to single-color
      if (this.isCustomMode) {
        this.exitCustomMode();
      }

      // Clear all selected states
      this.querySelectorAll('.is-selected').forEach((el) => el.classList.remove('is-selected'));
      btn.classList.add('is-selected');

      // Update displayed name
      if (this.nameDisplay) {
        this.nameDisplay.textContent = colorName;
      }

      // Update lid state (all same color)
      const rgb = this.getColorRgbByName(colorName);
      this.defaultColor = { name: colorName, rgb: rgb };
      this.lidColors = Array(this.lidCount).fill(null).map(() => ({
        name: colorName, rgb: rgb
      }));
      this.baseColor = { name: 'White', rgb: 'rgb(255, 255, 255)' };

      // Clear line item properties
      this.clearLineItemProperties();

      // Persist open state before variant change
      if (this.workshopWrap && this.workshopWrap.classList.contains('is-open')) {
        this.persistOpenState(true);
      }

      // Fire the variant change
      const radio = this.querySelector('#' + CSS.escape(radioId));
      if (radio && !radio.checked) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    // ---- Custom mode (per-lid) ----

    toggleCustomMode() {
      if (this.isCustomMode) {
        this.exitCustomMode();
      } else {
        this.enterCustomMode(true);
      }
    }

    enterCustomMode(animate = true) {
      this.isCustomMode = true;
      this.activeComponentIdx = 0;
      this.visitedLids = new Set();

      this.classList.add('is-custom-mode-active');
      if (this.upsellBtn) this.upsellBtn.classList.add('is-active');
      if (this.lidBuilderWrap) {
        this.lidBuilderWrap.classList.add('is-open');
      }

      this.persistCustomMode(true);
      this.updateToyVisual();
      this.updatePaletteSelection();
      this.switchToCustomVariant();
      this.updateLineItemProperties();
    }

    exitCustomMode() {
      this.isCustomMode = false;

      this.classList.remove('is-custom-mode-active');
      if (this.upsellBtn) this.upsellBtn.classList.remove('is-active');
      if (this.lidBuilderWrap) {
        this.lidBuilderWrap.classList.remove('is-open');
      }

      this.persistCustomMode(false);
      this.clearLineItemProperties();
      this.switchToStandardVariant();
    }

    // ---- SVG toy click handling ----

    handleToyClick(e) {
      // Check if a lid was clicked
      const lidGroup = e.target.closest('[data-toy-lid]');
      if (lidGroup) {
        this.activeComponentIdx = parseInt(lidGroup.dataset.toyLid);
        this.updateToyVisual();
        this.updatePaletteSelection();
        return;
      }

      // Check if the board (base) was clicked
      const base = e.target.closest('[data-toy-base]');
      const baseRing = e.target.closest('[data-toy-base-ring]');
      if (base || baseRing) {
        this.activeComponentIdx = this.lidCount; // base index
        this.updateToyVisual();
        this.updatePaletteSelection();
        return;
      }
    }

    updateToyVisual() {
      if (!this.toySvg) return;

      // Update lid fills
      const lidGroups = this.toySvg.querySelectorAll('[data-toy-lid]');
      lidGroups.forEach((group) => {
        const idx = parseInt(group.dataset.toyLid);
        const lid = group.querySelector('.color-picker__toy-lid');
        if (lid && this.lidColors[idx]) {
          lid.style.fill = this.lidColors[idx].rgb;
        }

        // Active ring
        group.classList.toggle('is-active', idx === this.activeComponentIdx);
      });

      // Update base — use style.fill so it overrides the CSS fill property
      // (CSS fill beats the SVG fill attribute in the cascade)
      const board = this.toySvg.querySelector('[data-toy-base]');
      if (board) {
        board.style.fill = this.baseColor.rgb;
      }

      // Base ring
      const baseRing = this.toySvg.querySelector('[data-toy-base-ring]');
      if (baseRing) {
        baseRing.classList.toggle('is-active', this.activeComponentIdx === this.lidCount);
      }

      // Update active label
      if (this.activeLabel) {
        if (this.activeComponentIdx < this.lidCount) {
          // Lid label — replace __N__ in the translated template
          const num = this.activeComponentIdx + 1;
          this.activeLabel.textContent = this.lidLabelTemplate.replace('__N__', num);
        } else {
          this.activeLabel.textContent = this.baseLabelText;
        }
      }
    }

    // ---- Reset handler ----

    handleReset() {
      // Reset all lids to default color
      this.lidColors = Array(this.lidCount).fill(null).map(() => ({
        name: this.defaultColor.name,
        rgb: this.defaultColor.rgb
      }));
      this.baseColor = { name: 'White', rgb: 'rgb(255, 255, 255)' };
      this.activeComponentIdx = 0;
      // Clear the visited-lids tracker so the smart auto-advance starts fresh.
      // Without this, every lid index from the previous session is still
      // remembered as "visited" and the very next color pick jumps to the base.
      this.visitedLids = new Set();

      this.updateToyVisual();
      this.updatePaletteSelection();
      this.updateLineItemProperties();
    }

    // ---- Palette handling (per-lid) ----

    handlePaletteClick(e) {
      const btn = e.target.closest('[data-lid-color]');
      if (!btn) return;

      const colorName = btn.dataset.lidColor;
      const colorRgb = btn.dataset.lidColorRgb;
      if (!colorName) return;

      // Set the colour on the active component, and mark the lid as visited
      // so the wrap-around logic below knows it's been handled.
      if (this.activeComponentIdx < this.lidCount) {
        this.lidColors[this.activeComponentIdx] = { name: colorName, rgb: colorRgb };
        this.visitedLids.add(this.activeComponentIdx);
      } else {
        this.baseColor = { name: colorName, rgb: colorRgb };
      }

      // Auto-advance: walk forward looking for the next unvisited lid, wrapping
      // around to lid 0 when we pass the end. Only when EVERY lid has been
      // visited do we jump to the base, so a user who started at lid 3 still
      // gets prompted for lids 1 and 2 before being asked about the base.
      if (this.activeComponentIdx < this.lidCount) {
        if (this.visitedLids.size >= this.lidCount) {
          // All lids done -> move to the base.
          this.activeComponentIdx = this.lidCount;
        } else {
          // Find the next unvisited lid, wrapping past lid (lidCount-1) back to 0.
          let next = this.activeComponentIdx;
          for (let i = 0; i < this.lidCount; i++) {
            next = (next + 1) % this.lidCount;
            if (!this.visitedLids.has(next)) {
              this.activeComponentIdx = next;
              break;
            }
          }
        }
      }

      this.updateToyVisual();
      this.updatePaletteSelection();
      this.updateLineItemProperties();
    }

    updatePaletteSelection() {
      if (!this.paletteContainer) return;

      const currentColor = this.activeComponentIdx < this.lidCount
        ? this.lidColors[this.activeComponentIdx]?.name
        : this.baseColor.name;

      const swatches = this.paletteContainer.querySelectorAll('[data-lid-color]');
      swatches.forEach((s) => {
        s.classList.toggle('is-selected', s.dataset.lidColor === currentColor);
      });
    }

    // ---- Variant switching ----

    switchToCustomVariant() {
      // Find and check the "Custom" variant radio
      const customRadio = this.querySelector('[data-variant-type="custom"]');
      if (customRadio && !customRadio.checked) {
        customRadio.checked = true;
        customRadio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    switchToStandardVariant() {
      // The standard variant change will be triggered by the next swatch click
      // (which fires the radio change event). Nothing extra needed here.
    }

    // ---- Line item properties ----

    updateLineItemProperties() {
      if (!this.productForm) return;

      for (let i = 0; i < this.lidCount; i++) {
        this.setLineItemProperty(`Lid ${i + 1}`, this.lidColors[i]?.name || '');
      }
      this.setLineItemProperty('Base', this.baseColor.name);
    }

    clearLineItemProperties() {
      if (!this.productForm) return;

      for (let i = 0; i < this.lidCount; i++) {
        this.setLineItemProperty(`Lid ${i + 1}`, '');
      }
      this.setLineItemProperty('Base', '');
    }

    setLineItemProperty(name, value) {
      if (!this.productForm) return;

      const inputName = `properties[${name}]`;
      let input = this.productForm.querySelector(`input[name="${inputName}"]`);

      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = inputName;
        this.productForm.appendChild(input);
      }

      input.value = value;
    }

    // ---- Helpers ----

    /**
     * Get the RGB color string for a color name by reading it from
     * the palette button's data-lid-color-rgb attribute.
     */
    getColorRgbByName(name) {
      if (!this.paletteContainer) return 'rgb(158, 158, 158)';

      const btn = this.paletteContainer.querySelector(`[data-lid-color="${name}"]`);
      if (btn && btn.dataset.lidColorRgb) {
        return btn.dataset.lidColorRgb;
      }

      return 'rgb(158, 158, 158)';
    }
  }

  customElements.define('color-picker-foraging', ColorPickerForaging);
}
